<script setup>
import { computed, ref } from 'vue'
import { CRITERIA } from '../lib/criteria.js'
import { money, num, pct } from '../lib/format.js'
import LeadsTable from './LeadsTable.vue'

const props = defineProps({
  group: { type: Object, required: true },
  locationId: { type: String, default: '' },
  leadFilter: { type: Function, default: null },
})

const abierto = ref(false)

const leads = computed(() =>
  props.leadFilter ? props.group.leads.filter(props.leadFilter) : props.group.leads,
)

const motivos = computed(() =>
  Object.values(CRITERIA)
    .map((c) => ({ ...c, count: props.group.byReason[c.id] || 0 }))
    .filter((c) => c.count > 0)
    .sort((a, b) => b.count - a.count),
)

const maxEtapa = computed(() => Math.max(1, ...props.group.stages.map((s) => s.count)))

/** Rojo si mas de un tercio del pipeline esta sin gestionar. */
const tono = computed(() => {
  if (props.group.rate >= 0.34) return 'critico'
  if (props.group.rate >= 0.15) return 'alto'
  return 'medio'
})
</script>

<template>
  <section class="card pipeline">
    <header>
      <div class="grow">
        <h2>{{ group.name }}</h2>
        <p class="muted sub">
          {{ num(group.unmanaged) }} de {{ num(group.totalItems) }} sin gestionar ·
          media {{ group.avgDaysStuck }} d sin avanzar · peor caso {{ group.maxDaysStuck }} d
        </p>
      </div>
      <div class="cifras">
        <div>
          <strong :class="tono">{{ pct(group.rate) }}</strong>
          <span class="muted">del pipeline</span>
        </div>
        <div>
          <strong>{{ money(group.value) }}</strong>
          <span class="muted">en riesgo</span>
        </div>
      </div>
    </header>

    <div class="cuerpo">
      <div class="col">
        <h3>Por etapa</h3>
        <div v-if="group.stages.length" class="etapas">
          <div v-for="s in group.stages" :key="s.id" class="etapa">
            <span class="nombre">{{ s.name }}</span>
            <span class="barra"><i :style="{ width: (s.count / maxEtapa * 100) + '%' }" /></span>
            <span class="mono valor">{{ num(s.count) }}</span>
          </div>
        </div>
        <p v-else class="muted">Sin etapas: estos contactos no entraron a ningun pipeline.</p>
      </div>

      <div class="col">
        <h3>Por motivo</h3>
        <ul class="motivos">
          <li v-for="m in motivos" :key="m.id" :title="m.help">
            <span class="punto" :style="{ background: m.color }" />
            <span class="grow">{{ m.label }}</span>
            <span class="mono">{{ num(m.count) }}</span>
          </li>
          <li v-if="!motivos.length" class="muted">Sin incidencias.</li>
        </ul>
        <div class="sev">
          <span class="badge sev-critico">{{ num(group.bySeverity.critico) }} criticos</span>
          <span class="badge sev-alto">{{ num(group.bySeverity.alto) }} altos</span>
          <span class="badge sev-medio">{{ num(group.bySeverity.medio) }} medios</span>
        </div>
      </div>
    </div>

    <footer>
      <button class="ghost no-print" @click="abierto = !abierto">
        {{ abierto ? 'Ocultar detalle' : `Ver detalle (${num(leads.length)} leads)` }}
      </button>
    </footer>

    <LeadsTable v-if="abierto" :leads="leads" :location-id="locationId" />
  </section>
</template>

<style scoped>
.pipeline { margin-bottom: 14px; overflow: hidden; }
header { display: flex; gap: 16px; align-items: flex-start; padding: 14px 16px; border-bottom: 1px solid var(--border); }
.sub { margin: 3px 0 0; font-size: 12.5px; }
.cifras { display: flex; gap: 22px; text-align: right; }
.cifras div { display: grid; }
.cifras strong { font-size: 19px; letter-spacing: -.01em; }
.cifras strong.critico { color: var(--critico); }
.cifras strong.alto { color: var(--alto); }
.cifras span { font-size: 11.5px; }

.cuerpo { display: grid; grid-template-columns: 1.4fr 1fr; gap: 24px; padding: 14px 16px; }
@media (max-width: 760px) { .cuerpo { grid-template-columns: 1fr; } header { flex-direction: column; } .cifras { text-align: left; } }
.col { display: grid; gap: 8px; align-content: start; }

.etapas { display: grid; gap: 6px; }
.etapa { display: grid; grid-template-columns: minmax(110px, 180px) 1fr 44px; gap: 10px; align-items: center; font-size: 12.5px; }
.barra { height: 7px; background: var(--surface-2); border-radius: 999px; overflow: hidden; }
.barra i { display: block; height: 100%; background: var(--accent); border-radius: 999px; }
.valor { text-align: right; }

.motivos { list-style: none; margin: 0; padding: 0; display: grid; gap: 5px; font-size: 12.5px; }
.motivos li { display: flex; gap: 8px; align-items: center; }
.punto { width: 8px; height: 8px; border-radius: 50%; flex: none; }
.sev { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 4px; }

footer { padding: 6px 10px; border-top: 1px solid var(--border); background: var(--surface-2); }
</style>
